/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, Grid, IconButton, InputLabel, LinearProgress, Link, List, ListItem, ListItemSecondaryAction, ListItemText, Menu, MenuItem, Paper, Select, SelectChangeEvent, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { KeyboardArrowDown, KeyboardArrowUp, Refresh } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { 
    GridFilterModel, GridToolbarFilterButton, GridToolbarContainer, 
    GridToolbarColumnsButton, GridToolbarDensitySelector, 
    GridColDef, GridSortModel 
} from '@mui/x-data-grid-pro';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Swal from 'sweetalert2';
// import { getRowsPerPageOptions } from '../components/AddEdit/CustomFunctions';
import { writeXlsxFile, formatUtcToLocal } from "../helper/utils";
import { InboxMessageBody } from '../helper/inboxMessageHtml';
import { apiClient } from '../helper/api';
import useAuth from '../context/AuthProvider';
import { PartnerType } from '../models/PartnersModel';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaDownload, FaRegEye, FaUpload } from 'react-icons/fa';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import { LightBlueBtnColor, DarkBlueBtnColor, HoverUnselectedLightBlueBtnColor, WhiteColor, GreenBtnColor, OrangeBtnColor, PrimaryBlueColor } from '../helper/theme';
import { AppRoutes } from '../helper/appRoutes';
import { CLIENT_INBOX_UPDATED_EVENT } from '../components/Icons/InboxNotificationBell';
import { DocumentViewer } from '../components/Elements/DocumentViewer';
import { UploadReportTagDocuments } from '../components/Elements/UploadReportTagDocuments';
import { UploadSignatureRequestDocuments } from '../components/Elements/UploadSignatureRequestDocuments';
import { ReportTagModel, ErrorTagCodeModel } from './ReportTag';
import JSZip from 'jszip';
import { CheckCircle } from '@mui/icons-material';
import { RiExpandUpDownLine, RiEye2Line } from "react-icons/ri";
import { RiEyeCloseLine } from "react-icons/ri";
import { RiArchiveLine } from "react-icons/ri";




export enum CategoryNames {
    TagNotification = 'Tag Notification',
    SignatureRequest = 'Signature Request',
    StampedDocuments = 'Stamped Documents',
    DiligentSearch = 'Diligent Search',
    NonPremiumEndorsement = 'Non-Premium Endorsement',
}

/** State-specific info for stamped documents: standard message MISC sends when emailing (SLA/DOIs requirements). */
const STAMPED_DOCUMENT_STATE_INFO: Record<string, { message: string; linkUrl: string }> = {
    Illinois: {
        message: 'Hello! Enclosed, please find Illinois SLA Countersignature page(s) for filings completed recently. For more information on the SLAI\'s requirements, please see here:',
        linkUrl: 'https://www.slai.org/docs/surplus-line-filing-taxes/reporting-policies-endorsements-other/',
    },
    'New York': {
        message: 'Hello! Enclosed, please find New York Stamped Declarations page(s) for filings completed recently. For more information on ELANY\'s requirements, please see here:',
        linkUrl: 'https://www.elany.org/publications-and-resources/publications/elany-says?url=dec-pages---binders---coverage-documents',
    },
};

function getStampedDocumentStateInfo(documentType: string | undefined): { message: string; linkUrl: string } | null {
    if (!documentType || typeof documentType !== 'string') return null;
    if (documentType.includes('Illinois')) return STAMPED_DOCUMENT_STATE_INFO.Illinois;
    if (documentType.includes('New York')) return STAMPED_DOCUMENT_STATE_INFO['New York'];
    return null;
}
export interface CustomerInboxModel {
    inboxId: number;
    partnersId: number;
    partnerName: string;
    userId: number;
    userName: string;

    reportTagId: number;
    stampedDocumentDetailId: number;
    signatureRequestId: number;
    diligentSearchLibraryId: number;
    nonPremiumEndorsementId?: number | null;
    message: string;
    clientInboxCategoryId: number;
    categoryName: string;

    isRead: boolean;
    isArchived: boolean;
    
    createdDate: string;
    createdBy: number;
    updatedDate: string;
    rowNum?: number;
    parentInboxId?: number | null;
    messageText?: string | null;
    errorTagList?: string | null;
    clientInHouseCloseRequestedAt?: string | null;
};
export interface ClientInboxResponse{
    clientInbox: CustomerInboxModel[];
    totalCount: number;
    totalPages: number;
    pageSize: number;
    pageNumber: number;
};
export interface CategoryModel{
    clientInboxCategoryId: number;
    categoryName: string;
};
const VenbrookPartnersId = 138;

export const CustomerInbox: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const token = user?.token;

    // DS-only users see only Diligent Search; hide category/client filter (API already restricts to category 4)
    const userRoles = (user?.userRoles || '').split(',').map((s: string) => s.trim()).filter(Boolean);
    const isDiligentSearchOnlyUser = userRoles.length === 1 && userRoles[0] === 'Diligent Search User';

    const [isPartnersFilter, setIsPartnersFilter] = useState<Boolean>(false);
    const [loading, setLoading] = useState(false);
    const [viewLoadingId, setViewLoadingId] = useState<number | null>(null);
    const [partners, setPartners] = useState<PartnerType[]>([{ partnersId: 0, customerName: '' }]);
    // const [isDiligentSearchUser, setIsDiligentSearchUser] = useState<boolean>(false);
    const [customerInbox, setCustomerInbox] = useState<CustomerInboxModel[]>([]);
    const [clientInboxCategories, setClientInboxCategories] = useState<CategoryModel[]>([]);

    const [totalCount, setTotalCount] = useState(0);
    const [pageSize, setPageSize] = useState(100);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRows, setSelectedRows] = useState<CustomerInboxModel[]>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({
        items: [],
    });
    // Default: newest first for both New Messages and Archive (Outlook-type expectation)
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'createdDate', sort: 'desc' }]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedClient, setSelectedClient] = useState<number | ''>('');

    const [selectedTab, setSelectedTab] = useState<'New Messages' | 'Archive'>('New Messages');
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null);

    const [dataLoaded, setDataLoaded] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(true);

    useEffect(() => {
        // setIsLoading(true);
        // alert("NewCompletedFilingsCL: " + NewCompletedFilingsCL?.getCompletedFilings_Respdata.length);
        if (customerInbox?.length > 0) {
            setDataLoaded(true);
        }
    }, [customerInbox]);

    useEffect(() => {
        if (dataLoaded) {
            setIsLoading(false);
        }
    }, [dataLoaded]);

    const handleClickOpenInboxActionOptions = (inboxId: number) => {
        const buttonEl = document.getElementById(`inbox-action-button-${inboxId}`) as HTMLElement | null;
        setActionMenuAnchorEl((prev) => (prev?.id === `inbox-action-button-${inboxId}` ? null : buttonEl));
    };
    const handleCloseInboxActionOptions = () => {
        setActionMenuAnchorEl(null);
    };

    const handleTabChange = (tab: 'New Messages' | 'Archive') => {
        if (tab !== selectedTab) {
            setSelectedTab(tab);
            setCurrentPage(1); // Reset to first page when tab changes
        }
    };

    const handleCategoryChange = (e: SelectChangeEvent) => {
        const newCategory = e.target.value;
        setSelectedCategory(newCategory);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    const handleClientChange = (e: SelectChangeEvent) => {
        const value = e.target.value;
        const newClient = value === '' ? '' : Number(value);
        setSelectedClient(newClient);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    const handleClear = () => {
        setSelectedCategory('');
        setSelectedClient('');
        setCurrentPage(1); // Reset to first page when clearing filters
    };

    const markAsRead = () => {
        // only if all selected rows are unread
        if(selectedRows.length > 0) {
            setLoading(true);
            const selectedRowsIds = selectedRows.map(row => row.inboxId);
            Swal.fire({
                title: '<div style="color: white;">Are you sure?</div>',
                html: '<div style="color: white;">You want to mark these messages as read?</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                confirmButtonText: 'Yes',
            }).then((result) => {
                if(result.isConfirmed) {
                    // call api to mark as read 
                    apiClient(`/clientInbox/markAsRead?clientInboxIdList=${selectedRowsIds.join(',')}`, {
                        method: 'POST',
                        headers: new Headers({
                            Authorization: `Bearer ${token}`,
                        }),
                    }).then((responseJson) => {
                        if (!responseJson) return;
                        // refresh the customer inbox after flipping read status
                        fetchCustomerInbox();
                        window.dispatchEvent(new CustomEvent(CLIENT_INBOX_UPDATED_EVENT));
                        setSelectedRows([]);
                        Swal.fire({
                            title: 'Messages marked as read',
                            text: 'The selected messages have been marked as read',
                            icon: 'success',
                        });
                    }).catch(error => {
                        console.error('Error updating read status:', error);
                    }).finally(() => {
                        setLoading(false);
                        setSelectedRows([]);
                    });
                }
            }).finally(() => {
                setLoading(false);
                setSelectedRows([]);
            });
        } else {
            Swal.fire({
                title: '<div style="color: white;">No messages selected</div>',
                html: '<div style="color: white;">Please select at least one message to mark as read</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                showConfirmButton: false,
            });
        }
    };

    /** Mark a single inbox item as read (e.g. when user opens a notification). No confirmation. */
    const markInboxItemAsRead = (inboxId: number) => {
        if (!inboxId) return;
        apiClient(`/clientInbox/markAsRead?clientInboxIdList=${inboxId}`, {
            method: 'POST',
            headers: new Headers({ Authorization: `Bearer ${token}` }),
        }).then(() => {
            fetchCustomerInbox();
            window.dispatchEvent(new CustomEvent(CLIENT_INBOX_UPDATED_EVENT));
        }).catch((err) => {
            console.error('Error marking inbox item as read:', err);
        });
    };

    const markAsUnread = () => {
        // only if all selected rows are read
        if(selectedRows.length > 0) {
            setLoading(true);
            const selectedRowsIds = selectedRows.map(row => row.inboxId);
            Swal.fire({
                title: '<div style="color: white;">Are you sure?</div>',
                html: '<div style="color: white;">You want to mark these messages as unread?</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                confirmButtonText: 'Yes',
            }).then((result) => {
                if(result.isConfirmed) {
                    // call api to mark as unread
                    apiClient(`/clientInbox/markAsUnread?clientInboxIdList=${selectedRowsIds.join(',')}`, {
                        method: 'POST',
                        headers: new Headers({
                            Authorization: `Bearer ${token}`,
                        }),
                    }).then((responseJson) => {
                        if (!responseJson) return;
                        // refresh the customer inbox after flipping read status
                        fetchCustomerInbox();
                        window.dispatchEvent(new CustomEvent(CLIENT_INBOX_UPDATED_EVENT));
                        setSelectedRows([]);
                        Swal.fire({
                            title: 'Messages marked as unread',
                            text: 'The selected messages have been marked as unread',
                            icon: 'success',
                        });
                    }).catch(error => {
                        console.error('Error updating read status:', error);
                    }).finally(() => {
                        setLoading(false);
                        setSelectedRows([]);
                    });
                }
            }).finally(() => {
                setLoading(false);
                setSelectedRows([]);
            });
        } else {
            Swal.fire({
                title: '<div style="color: white;">No messages selected</div>',
                html: '<div style="color: white;">Please select at least one message to mark as unread</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                showConfirmButton: false,
            });
        }
    };
    const markAsArchived = () => {
        // only if all selected rows are unarchived
        if(selectedRows.length > 0 && selectedRows.every(row => row.isArchived === false)) {
            setLoading(true);
            const selectedRowsIds = selectedRows.map(row => row.inboxId);
            Swal.fire({
                title: '<div style="color: white;">Are you sure?</div>',
                html: '<div style="color: white;">You want to move these messages to archive?</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                confirmButtonText: 'Yes',
            }).then((result) => {
                if(result.isConfirmed) {
                    // call api to move to archive
                    apiClient(`/clientInbox/markAsArchived?clientInboxIdList=${selectedRowsIds.join(',')}`, {
                        method: 'POST',
                        headers: new Headers({
                            Authorization: `Bearer ${token}`,
                        }),
                    }).then((responseJson) => {
                        if (!responseJson) return;
                        // refresh the customer inbox after moving to archive
                        fetchCustomerInbox();
                        window.dispatchEvent(new CustomEvent(CLIENT_INBOX_UPDATED_EVENT));
                        setSelectedRows([]);
                        Swal.fire({
                            title: 'Messages marked as archived',
                            text: 'The selected messages have been marked as archived',
                            icon: 'success',
                        });
                    }).catch(error => {
                        console.error('Error marking as archived:', error);
                    }).finally(() => {
                        setLoading(false);
                        setSelectedRows([]);
                    });
                }
            }).finally(() => {
                setLoading(false);
                setSelectedRows([]);
            });
        } else {
            Swal.fire({
                title: '<div style="color: white;">No messages selected</div>',
                html: '<div style="color: white;">Please select at least one message to mark as archived</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                showConfirmButton: false,
            });
        }
    };
    const markAsUnarchived = () => {
        // only if all selected rows are archived
        if(selectedRows.length > 0 && selectedRows.every(row => row.isArchived === true)) {
            setLoading(true);
            const selectedRowsIds = selectedRows.map(row => row.inboxId);
            Swal.fire({
                title: '<div style="color: white;">Are you sure?</div>',
                html: '<div style="color: white;">You want to mark these messages as unarchived?</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                confirmButtonText: 'Yes',
            }).then((result) => {
                if(result.isConfirmed) {
                    // call api to mark as unarchived
                    apiClient(`/clientInbox/markAsUnarchived?clientInboxIdList=${selectedRowsIds.join(',')}`, {
                        method: 'POST',
                        headers: new Headers({
                            Authorization: `Bearer ${token}`,
                        }),
                    }).then((responseJson) => {
                        if (!responseJson) return;
                        // refresh the customer inbox after marking as unarchived
                        fetchCustomerInbox();
                        window.dispatchEvent(new CustomEvent(CLIENT_INBOX_UPDATED_EVENT));
                        Swal.fire({
                            title: 'Messages marked as unarchived',
                            text: 'The selected messages have been marked as unarchived',
                            icon: 'success',
                        });
                    }).catch(error => {
                        console.error('Error marking as unarchived:', error);
                    }).finally(() => {
                        setLoading(false);
                        setSelectedRows([]);
                    });
                }
            }).finally(() => {
                setLoading(false);
                setSelectedRows([]);
            });
        } else {
            Swal.fire({
                title: '<div style="color: white;">No messages selected</div>',
                html: '<div style="color: white;">Please select at least one message to mark as unarchived</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                showConfirmButton: false,
            });
        }
    };

    const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
        setFilterModel(newFilterModel);
        setCurrentPage(1);  // Reset to first page when filter changes
    };
    const handleSortModelChange = (newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
        setCurrentPage(1);  // Reset to first page when sort changes
    };

    const handleCategoryClick = (row: CustomerInboxModel) => {
        switch (row.categoryName) {
            case CategoryNames.TagNotification:
                break;
            case CategoryNames.SignatureRequest:
                navigate(AppRoutes.home.requestSignature);
                break;
            case CategoryNames.StampedDocuments:
                navigate(AppRoutes.home.sendStampedDocument);
                break;
            case CategoryNames.DiligentSearch:
                navigate(AppRoutes.home.diligentSearchLibrary);
                break;
        };
    };

    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [documentsToView, setDocumentsToView] = useState<{documentUrl: string, documentName: string}[]>([]);
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
    const handleCloseDocumentViewer = () => {
        setDocumentViewerOpen(false);
        setDocumentsToView([]);
        setCurrentDocumentIndex(0);
    };

    /** Stamped document info popup (MISC feedback: show state/SLA/DOIs requirements to client) */
    const [stampedDocumentInfoOpen, setStampedDocumentInfoOpen] = useState(false);
    const [stampedDocumentInfoContent, setStampedDocumentInfoContent] = useState<{ message: string; linkUrl: string } | null>(null);
    const [pendingStampedDocuments, setPendingStampedDocuments] = useState<{ documentUrl: string; documentName: string }[]>([]);
    const handleCloseStampedDocumentInfo = () => {
        setStampedDocumentInfoOpen(false);
        setStampedDocumentInfoContent(null);
        setPendingStampedDocuments([]);
    };

    const [nonPremiumRejectionDialog, setNonPremiumRejectionDialog] = useState<{
        open: boolean;
        messageText: string;
        documents: { documentName: string; documentUrl: string }[];
    }>({
        open: false,
        messageText: '',
        documents: [],
    });
    const handleCloseNonPremiumRejectionInfo = () => {
        setNonPremiumRejectionDialog({
            open: false,
            messageText: '',
            documents: [],
        });
    };
    const openNonPremiumRejectionView = (inboxId: number) => {
        markInboxItemAsRead(inboxId);
        setViewLoadingId(inboxId);
        setLoading(true);
        apiClient(`/clientInbox/getNonPremiumEndorsementInboxDetail?inboxId=${inboxId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }),
        })
            .then((response) => {
                if (!response || !response.data) {
                    Swal.fire({
                        title: 'Not Found',
                        text: response?.message || 'Unable to load rejection details.',
                        icon: 'info',
                        confirmButtonText: 'OK',
                    });
                    return;
                }
                const detail = response.data;
                const documents = detail.blobAddress
                    ? [{
                        documentName: detail.documentName || 'Document',
                        documentUrl: detail.blobAddress,
                    }]
                    : [];
                setNonPremiumRejectionDialog({
                    open: true,
                    messageText: detail.messageText || '',
                    documents,
                });
            })
            .catch((error) => {
                console.error('Error loading non-premium rejection inbox detail:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to load rejection details.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            })
            .finally(() => {
                setLoading(false);
                setViewLoadingId(null);
            });
    };
    const handleViewStampedDocumentsAfterInfo = () => {
        if (pendingStampedDocuments.length > 0) {
            setDocumentsToView(pendingStampedDocuments);
            setCurrentDocumentIndex(0);
            setDocumentViewerOpen(true);
        }
        handleCloseStampedDocumentInfo();
    };

    // Single Attached Documents dialog (shared for Report Tag, Signature Request, etc.)
    type AttachedDocumentsContext = { type: 'reportTag' | 'signatureRequest'; id: number } | null;
    const [attachedDocumentsOpen, setAttachedDocumentsOpen] = useState(false);
    const [attachedDocumentsList, setAttachedDocumentsList] = useState<{ id: string | number; documentName: string; documentUrl: string; createdDate?: string; sentBy?: string }[]>([]);
    const [attachedDocumentsContext, setAttachedDocumentsContext] = useState<AttachedDocumentsContext>(null);
    const handleCloseAttachedDocuments = () => {
        setAttachedDocumentsOpen(false);
        setAttachedDocumentsList([]);
        setAttachedDocumentsContext(null);
    };
    const viewDocumentFromList = (documentUrl: string, documentName: string) => {
        setDocumentsToView([{ documentUrl, documentName }]);
        setCurrentDocumentIndex(0);
        setDocumentViewerOpen(true);
    };
    const downloadSingleDocument = async (documentUrl: string, documentName: string) => {
        try {
            const response = await fetch(documentUrl, { method: 'GET', mode: 'cors', credentials: 'omit', cache: 'no-cache' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = documentName || 'document';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            Swal.fire({ title: 'Error', text: error?.message || 'Failed to download document', icon: 'error', confirmButtonText: 'OK' });
        }
    };

    const [reportTag, setReportTag] = useState<ReportTagModel | null>(null);
    const [uploadReportTagDocumentsOpen, setUploadReportTagDocumentsOpen] = useState(false);
    const handleCloseUploadReportTagDocuments = () => {
        setUploadReportTagDocumentsOpen(false);
        setReportTag(null);
        setClientDocumentData({ reportTagId: 0, files: [] });
        fetchCustomerInbox();
    };
    const [uploadSignatureRequestOpen, setUploadSignatureRequestOpen] = useState(false);
    const [selectedSignatureRequestIdForUpload, setSelectedSignatureRequestIdForUpload] = useState<number>(0);
    const handleCloseUploadSignatureRequest = () => {
        setUploadSignatureRequestOpen(false);
        setSelectedSignatureRequestIdForUpload(0);
        fetchCustomerInbox();
    };

    /** Signature Request info popup (MISC details: licensee, state, form, due date, signature type, notarization) */
    type SignatureRequestInfoContent = {
        licensee: string;
        state: string;
        nameOfForm: string;
        dueDate: string;
        signatureType: string;
        notarizationRequired: string;
    };
    type SignatureRequestDialogState = {
        open: boolean;
        content: SignatureRequestInfoContent | null;
        signatureRequestId: number;
        documents: { id: string | number; documentName: string; documentUrl: string; createdDate?: string; sentBy?: string; createdByUserRole?: string }[];
    };
    const [signatureRequestDialog, setSignatureRequestDialog] = useState<SignatureRequestDialogState>({
        open: false,
        content: null,
        signatureRequestId: 0,
        documents: [],
    });
    const handleCloseSignatureRequestInfo = () => {
        setSignatureRequestDialog({ open: false, content: null, signatureRequestId: 0, documents: [] });
    };
    const handleViewSignatureRequestDocumentsFromInfo = () => {
        const docs = signatureRequestDialog.documents;
        if (docs.length === 0) return;
        if (docs.length === 1) {
            setDocumentsToView([{ documentUrl: docs[0].documentUrl, documentName: docs[0].documentName }]);
            setCurrentDocumentIndex(0);
            setDocumentViewerOpen(true);
        } else {
            setAttachedDocumentsList(docs);
            setAttachedDocumentsContext({ type: 'signatureRequest', id: signatureRequestDialog.signatureRequestId });
            setAttachedDocumentsOpen(true);
        }
        handleCloseSignatureRequestInfo();
    };
    const handleUploadSignatureRequestFromInfo = () => {
        setSelectedSignatureRequestIdForUpload(signatureRequestDialog.signatureRequestId);
        setUploadSignatureRequestOpen(true);
        handleCloseSignatureRequestInfo();
    };

    /** Tag Notification view popup (MESSAGE = tag summary + filer notes, ATTACHMENTS = report tag documents) */
    type TagNotificationDialogState = {
        open: boolean;
        reportTagId: number;
        inboxId: number;
        reportTag: ReportTagModel | null;
        errorTagCodes: ErrorTagCodeModel[];
        filerNotes: { filerNote: string; sentBy?: string; sentOn?: string; sentByUserRole?: string }[];
        documents: { id: string | number; documentName: string; documentUrl: string; createdDate?: string; sentBy?: string; createdByUserRole?: string }[];
    };
    const [tagNotificationDialog, setTagNotificationDialog] = useState<TagNotificationDialogState>({
        open: false,
        reportTagId: 0,
        inboxId: 0,
        reportTag: null,
        errorTagCodes: [],
        filerNotes: [],
        documents: [],
    });
    const [tagNotificationReplyToMisc, setTagNotificationReplyToMisc] = useState('');
    const handleCloseTagNotificationInfo = () => {
        setTagNotificationDialog({ open: false, reportTagId: 0, inboxId: 0, reportTag: null, errorTagCodes: [], filerNotes: [], documents: [] });
        setTagNotificationReplyToMisc('');
    };
    const handleReplyToMiscFromTag = () => {
        const replyToMisc = tagNotificationReplyToMisc?.trim();
        const inboxId = tagNotificationDialog.inboxId;
        if (!replyToMisc) return;
        if (tagNotificationDialog.reportTag?.clientInHouseCloseRequestedAt) {
            return;
        }
        setLoading(true);
        apiClient('/ReportTags/sendReplyToMisc', {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
            body: JSON.stringify({ replyToMisc, inboxId }),
        }).then((response) => {
            if (response?.status !== false) {
                Swal.fire({ title: 'Reply sent', text: 'Your reply has been successfully sent to MISC.', icon: 'success', confirmButtonText: 'OK' });
                setTagNotificationReplyToMisc('');
                
                handleCloseTagNotificationInfo();
                fetchCustomerInbox();
            } else {
                throw new Error(response?.message || 'Failed to send reply');
            }
        }).catch((err) => {
            console.error('Error sending reply to MISC:', err);
            Swal.fire({ title: 'Error', text: err?.message || 'Failed to send reply to MISC', icon: 'error', confirmButtonText: 'OK' });
        }).finally(() => setLoading(false));
    };
    const handleUploadDocumentFromTagNotificationInfo = () => {
        if (tagNotificationDialog.reportTag?.clientInHouseCloseRequestedAt) {
            return;
        }
        setClientDocumentData({ reportTagId: tagNotificationDialog.reportTagId, files: [] });
        fetchReportTag(tagNotificationDialog.reportTagId);
        setUploadReportTagDocumentsOpen(true);
        handleCloseTagNotificationInfo();
    };

    // const [reportTags, setReportTags] = useState<ReportTagModel[]>([]);
    const [clientDocumentData, setClientDocumentData] = useState<{ reportTagId: number, files: File[]; inboxId?: number }>({ reportTagId: 0, files: [] });

    //#region View and Download functions for Report Tags, Signature Requests, & Stamped Documents
    // View and Download functions for Report Tags
    const viewReportTagDocuments = (reportTagId: number, inboxId: number) => {
        markInboxItemAsRead(inboxId);
        setViewLoadingId(inboxId);
        setLoading(true);
        apiClient(`/ReportTags/getReportTagDocumentsById/${reportTagId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((response) => {
            if (!response || !response.data || !response.data.documents || response.data.documents.length === 0) {
                Swal.fire({
                    title: 'No Documents',
                    text: 'No documents found for this tag',
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
                return;
            }
            const documents = response.data.documents.map((doc: any, index: number) => ({
                id: doc.reportTagDocumentId ?? index,
                documentName: doc.documentName || 'Document',
                documentUrl: doc.documentUrl,
                createdDate: doc.dateCreated ?? doc.createdDate,
                sentBy: doc.createdByUsername ?? doc.createdBy ?? 'N/A',
                createdByUserRole: doc.createdByUserRole?? 'N/A',
            }));
            if (documents.length === 1) {
                setDocumentsToView([{ documentUrl: documents[0].documentUrl, documentName: documents[0].documentName }]);
                setCurrentDocumentIndex(0);
                setDocumentViewerOpen(true);
            } else {
                setAttachedDocumentsList(documents);
                setAttachedDocumentsContext({ type: 'reportTag', id: reportTagId });
                setAttachedDocumentsOpen(true);
            }
            if (documents.length === 1) {
                setDocumentsToView([{ documentUrl: documents[0].documentUrl, documentName: documents[0].documentName }]);
                setCurrentDocumentIndex(0);
                setDocumentViewerOpen(true);
            } else {
                setAttachedDocumentsList(documents);
                setAttachedDocumentsContext({ type: 'reportTag', id: reportTagId });
                setAttachedDocumentsOpen(true);
            }
        }).catch((error) => {
            console.error("Error fetching documents:", error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to fetch documents',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }).finally(() => {
            setLoading(false);
            setViewLoadingId(null);
        });
    };

    /** Open Tag Notification popup (MESSAGE = tag summary + filer notes, ATTACHMENTS = documents) */
    const openTagNotificationView = (reportTagId: number, inboxId: number) => {
        markInboxItemAsRead(inboxId);
        setViewLoadingId(inboxId);
        setLoading(true);
        const filerNotesPromise = apiClient(`/ReportTags/getFilerNotes?reportTagId=${reportTagId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
        }).then((res) => {
            const data = res?.data ?? [];
            return Array.isArray(data) ? data.map((item: { filerNote?: string; sentBy?: string; sentOn?: string; sentByUserRole?: string }) => ({
                filerNote: item.filerNote ?? '',
                sentBy: item.sentBy,
                sentOn: item.sentOn,
                sentByUserRole: item.sentByUserRole,
            })) : [];
        }).catch(() => []);
        const documentsPromise = apiClient(`/ReportTags/getReportTagDocumentsById/${reportTagId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
        }).then((response) => {
            if (!response?.data?.documents?.length) return [];
            return response.data.documents.map((doc: any, index: number) => ({
                id: doc.reportTagDocumentId ?? index,
                documentName: doc.documentName || 'Document',
                documentUrl: doc.documentUrl ?? doc.blobAddress ?? '',
                createdDate: doc.dateCreated ?? doc.createdDate,
                sentBy: doc.createdByUsername ?? doc.sentBy ?? doc.createdBy ?? 'N/A',
                createdByUserRole: doc.createdByUserRole?? 'N/A',
            }));
        }).catch(() => []);
        const reportTagPromise = apiClient(`/ReportTags/getReportTagById/${reportTagId}`, {
            method: 'GET',
            headers: new Headers({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }),
        }).then((res) => res?.data ?? null).catch(() => null);
        const errorTagCodesPromise = apiClient('/ReportTags/getAllErrorTags', {
            method: 'GET',
            headers: new Headers({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }),
        }).then((res) => Array.isArray(res?.data) ? res.data : []).catch(() => []);
        Promise.all([filerNotesPromise, documentsPromise, reportTagPromise, errorTagCodesPromise]).then(([filerNotes, documents, reportTag, errorTagCodes]) => {
            setTagNotificationDialog({ open: true, reportTagId, inboxId, reportTag, errorTagCodes, filerNotes, documents });
        }).finally(() => {
            setLoading(false);
            setViewLoadingId(null);
        });
    };

    const downloadReportTagDocuments = async (reportTagId: number) => {
        setLoading(true);
        try {
            const response = await apiClient(`/ReportTags/getReportTagDocumentsById/${reportTagId}`, {
                method: 'GET',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                })
            });

            if (!response || !response.data || !response.data.documents || response.data.documents.length === 0) {
                Swal.fire({
                    title: 'No Documents',
                    text: 'No documents found for this tag',
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
                setLoading(false);
                return;
            }

            const documents = response.data.documents;
            const folderName = `Report Tag #${reportTagId} - Documents`;

            Swal.fire({
                title: '<div style="color: white;">Download Documents</div>',
                html: `<div style="color: white;">This will download all documents to a zip file named <strong>${folderName}.zip</strong></div>`,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                background: PrimaryBlueColor,
            }).then(async (confirmResult) => {
                if (!confirmResult.isConfirmed) {
                    setLoading(false);
                    return;
                }

                Swal.fire({
                    title: 'Preparing Download...',
                    html: 'Please wait while we prepare your files for download.',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                const zip = new JSZip();
                let successCount = 0;
                let failCount = 0;
                const errors: string[] = [];
                const usedFileNames = new Set<string>();

                const generateUniqueFileName = (baseFileName: string): string => {
                    const fileExtension = baseFileName.includes('.') 
                        ? baseFileName.substring(baseFileName.lastIndexOf('.'))
                        : '';
                    const fileNameWithoutExt = baseFileName.includes('.')
                        ? baseFileName.substring(0, baseFileName.lastIndexOf('.'))
                        : baseFileName;
                    
                    let uniqueFileName = baseFileName;
                    let counter = 0;
                    while (usedFileNames.has(uniqueFileName)) {
                        counter++;
                        uniqueFileName = `${fileNameWithoutExt} (${counter})${fileExtension}`;
                    }
                    usedFileNames.add(uniqueFileName);
                    return uniqueFileName;
                };

                await Promise.all(
                    documents.map(async (doc: any) => {
                        try {
                            const response = await fetch(doc.documentUrl, {
                                method: 'GET',
                                mode: 'cors',
                                credentials: 'omit',
                                cache: 'no-cache',
                            });
                            
                            if (!response.ok) {
                                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                            }
                            
                            const blob = await response.blob();
                            const uniqueFileName = generateUniqueFileName(doc.documentName || `document_${doc.reportTagDocumentId}`);
                            zip.file(uniqueFileName, blob);
                            successCount++;
                        } catch (error: any) {
                            failCount++;
                            const errorMsg = error.message || 'Failed to download';
                            errors.push(`${doc.documentName}: ${errorMsg}`);
                            console.error(`Error downloading ${doc.documentName}:`, error);
                        }
                    })
                );

                if (successCount === 0) {
                    throw new Error('Failed to download any documents');
                }

                const zipBlob = await zip.generateAsync({ type: 'blob' });
                const url = window.URL.createObjectURL(zipBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${folderName}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                Swal.close();
                
                if (failCount > 0) {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Partial Success',
                        html: `
                            <p><strong>${successCount}</strong> document(s) downloaded successfully.</p>
                            <p><strong>${failCount}</strong> document(s) failed to download.</p>
                            ${errors.length > 0 ? `<p style="font-size: 12px; color: #666; margin-top: 10px;">Errors:<br>${errors.slice(0, 3).join('<br>')}${errors.length > 3 ? '<br>...' : ''}</p>` : ''}
                        `,
                        confirmButtonText: 'OK',
                    });
                } else {
                    Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: `All ${successCount} document(s) have been downloaded successfully.`,
                        confirmButtonText: 'OK',
                    });
                }
            }).catch((error) => {
                console.error('Error in download confirmation:', error);
            }).finally(() => {
                setLoading(false);
            });
        } catch (error: any) {
            console.error('Error in downloadReportTagDocuments:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'An error occurred while downloading the documents.',
                confirmButtonText: 'OK',
            });
            setLoading(false);
        }
    };

    // View and Download functions for Signature Requests: show MISC details popup (Outlook-like message) then View/Upload
    const viewSignatureRequestDocuments = (signatureRequestId: number, inboxId: number) => {
        markInboxItemAsRead(inboxId);
        setViewLoadingId(inboxId);
        setLoading(true);
        apiClient(`/SignatureRequests/getSignatureRequestById/${signatureRequestId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((response) => {
            if (!response || response.status === false || !response.data) {
                Swal.fire({ title: 'Error', text: 'Failed to load signature request', icon: 'error', confirmButtonText: 'OK' });
                return;
            }
            const payload = response.data ?? response;
            const sr = Array.isArray(payload?.signatureRequest) ? payload.signatureRequest[0] : payload?.signatureRequest;
            const docsRaw = payload?.documents ?? (payload as any)?.Documents ?? response?.documents ?? (response as any)?.Documents ?? [];
            const docs = Array.isArray(docsRaw) ? docsRaw : [];
            const licensee = sr?.signerUserFirstName && sr?.signerUserLastName
                ? `${sr.signerUserFirstName} ${sr.signerUserLastName}`.trim()
                : (sr?.signerUserName ?? '—');
            const signatureTypes: string[] = [];
            if (sr?.isWetSignature) signatureTypes.push('Wet');
            if (sr?.isElectronicSignature) signatureTypes.push('Electronic');
            if (sr?.isDigitalSignature) signatureTypes.push('Digital');
            const signatureType = signatureTypes.length > 0 ? signatureTypes.join(', ') : '—';
            const notarizationRequired = sr?.notarizationRequired != null ? (sr.notarizationRequired ? 'Yes' : 'No') : '—';
            const mappedDocs = docs.map((doc: any, index: number) => ({
                id: String(
                    doc.signatureRequestDocumentId ??
                    doc.signaturerequestdocumentid ??
                    doc.blobAddress ??
                    doc.blobaddress ??
                    `doc-${index}`
                ),
                documentName: doc.documentName ?? doc.documentname ?? 'Document',
                documentUrl: doc.blobAddress ?? doc.blobaddress ?? doc.documentUrl ?? doc.documenturl ?? '',
                createdDate: doc.createdDate ?? doc.createddate,
                sentBy: doc.createdByUsername ?? doc.createdbyusername ?? doc.createdBy ?? doc.createdby ?? 'N/A',
                createdByUserRole: doc.createdByUserRole ?? doc.createdbyuserrole ?? '',
            }));
            setSignatureRequestDialog({
                open: true,
                content: {
                    licensee,
                    state: sr?.state ?? '—',
                    nameOfForm: sr?.formType ?? '—',
                    dueDate: sr?.dueDate ?? '—',
                    signatureType,
                    notarizationRequired,
                },
                signatureRequestId: signatureRequestId,
                documents: mappedDocs,
            });
        }).catch((error) => {
            console.error("Error fetching signature request:", error);
            Swal.fire({ title: 'Error', text: 'Failed to fetch signature request', icon: 'error', confirmButtonText: 'OK' });
        }).finally(() => {
            setLoading(false);
            setViewLoadingId(null);
        });
    };

    const downloadSignatureRequestDocuments = async (signatureRequestId: number) => {
        setLoading(true);
        try {
            const response = await apiClient(`/SignatureRequests/getSignatureRequestDocumentsById/${signatureRequestId}`, {
                method: 'GET',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                })
            });

            if (!response || !response.data || !response.data.documents || response.data.documents.length === 0) {
                Swal.fire({
                    title: 'No Documents',
                    text: 'No documents found for this signature request',
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
                setLoading(false);
                return;
            }

            const documents = response.data.documents;
            const folderName = `Signature Request #${signatureRequestId} - Documents`;

            Swal.fire({
                title: '<div style="color: white;">Download Documents</div>',
                html: `<div style="color: white;">This will download all documents to a zip file named <strong>${folderName}.zip</strong></div>`,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                background: PrimaryBlueColor,
            }).then(async (confirmResult) => {
                if (!confirmResult.isConfirmed) {
                    setLoading(false);
                    return;
                }

                Swal.fire({
                    title: 'Preparing Download...',
                    html: 'Please wait while we prepare your files for download.',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                const zip = new JSZip();
                let successCount = 0;
                let failCount = 0;
                const errors: string[] = [];
                const usedFileNames = new Set<string>();

                const generateUniqueFileName = (baseFileName: string): string => {
                    const fileExtension = baseFileName.includes('.') 
                        ? baseFileName.substring(baseFileName.lastIndexOf('.'))
                        : '';
                    const fileNameWithoutExt = baseFileName.includes('.')
                        ? baseFileName.substring(0, baseFileName.lastIndexOf('.'))
                        : baseFileName;
                    
                    let uniqueFileName = baseFileName;
                    let counter = 0;
                    while (usedFileNames.has(uniqueFileName)) {
                        counter++;
                        uniqueFileName = `${fileNameWithoutExt} (${counter})${fileExtension}`;
                    }
                    usedFileNames.add(uniqueFileName);
                    return uniqueFileName;
                };

                await Promise.all(
                    documents.map(async (doc: any) => {
                        try {
                            const response = await fetch(doc.documentUrl, {
                                method: 'GET',
                                mode: 'cors',
                                credentials: 'omit',
                                cache: 'no-cache',
                            });
                            
                            if (!response.ok) {
                                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                            }
                            
                            const blob = await response.blob();
                            const uniqueFileName = generateUniqueFileName(doc.documentName || `document_${doc.signatureRequestDocumentId}`);
                            zip.file(uniqueFileName, blob);
                            successCount++;
                        } catch (error: any) {
                            failCount++;
                            const errorMsg = error.message || 'Failed to download';
                            errors.push(`${doc.documentName}: ${errorMsg}`);
                            console.error(`Error downloading ${doc.documentName}:`, error);
                        }
                    })
                );

                if (successCount === 0) {
                    throw new Error('Failed to download any documents');
                }

                const zipBlob = await zip.generateAsync({ type: 'blob' });
                const url = window.URL.createObjectURL(zipBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${folderName}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                Swal.close();
                
                if (failCount > 0) {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Partial Success',
                        html: `
                            <p><strong>${successCount}</strong> document(s) downloaded successfully.</p>
                            <p><strong>${failCount}</strong> document(s) failed to download.</p>
                            ${errors.length > 0 ? `<p style="font-size: 12px; color: #666; margin-top: 10px;">Errors:<br>${errors.slice(0, 3).join('<br>')}${errors.length > 3 ? '<br>...' : ''}</p>` : ''}
                        `,
                        confirmButtonText: 'OK',
                    });
                } else {
                    Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: `All ${successCount} document(s) have been downloaded successfully.`,
                        confirmButtonText: 'OK',
                    });
                }
            }).catch((error) => {
                console.error('Error in download confirmation:', error);
            }).finally(() => {
                setLoading(false);
            });
        } catch (error: any) {
            console.error('Error in downloadSignatureRequestDocuments:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'An error occurred while downloading the documents.',
                confirmButtonText: 'OK',
            });
            setLoading(false);
        }
    };

    // View and Download functions for Stamped Documents
    const viewStampedDocuments = (stampedDocumentDetailId: number, inboxId: number) => {
        markInboxItemAsRead(inboxId);
        setViewLoadingId(inboxId);
        setLoading(true);
        apiClient(`/StampedDocument/getStampedDocumentById/${stampedDocumentDetailId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((response) => {
            if (!response || !response.data || !response.data.documents || response.data.documents.length === 0) {
                Swal.fire({
                    title: 'No Documents',
                    text: 'No documents found for this stamped document',
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
                return;
            }
            const documents = response.data.documents.map((doc: any) => ({
                documentUrl: doc.blobAddress,
                documentName: doc.documentName || 'Document'
            }));
            const documentType = response.data?.stampedDocumentDetail?.documentType;
            const stateInfo = getStampedDocumentStateInfo(documentType);
            // Show state/SLA info popup when MISC indicated Illinois or New York, so client sees what was sent and requirements
            if (stateInfo) {
                setStampedDocumentInfoContent(stateInfo);
                setPendingStampedDocuments(documents);
                setStampedDocumentInfoOpen(true);
            } else {
                setDocumentsToView(documents);
                setCurrentDocumentIndex(0);
                setDocumentViewerOpen(true);
            }
        }).catch((error) => {
            console.error("Error fetching documents:", error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to fetch documents',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }).finally(() => {
            setLoading(false);
            setViewLoadingId(null);
        });
    };

    const downloadStampedDocuments = async (stampedDocumentDetailId: number) => {
        setLoading(true);
        try {
            const response = await apiClient(`/StampedDocument/getStampedDocumentById/${stampedDocumentDetailId}`, {
                method: 'GET',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                })
            });

            if (!response || !response.data || !response.data.documents || response.data.documents.length === 0) {
                Swal.fire({
                    title: 'No Documents',
                    text: 'No documents found for this stamped document',
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
                setLoading(false);
                return;
            }

            const documents = response.data.documents;
            const folderName = `Stamped Document #${stampedDocumentDetailId} - Documents`;

            Swal.fire({
                title: '<div style="color: white;">Download Documents</div>',
                html: `<div style="color: white;">This will download all documents to a zip file named <strong>${folderName}.zip</strong></div>`,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                background: PrimaryBlueColor,
            }).then(async (confirmResult) => {
                if (!confirmResult.isConfirmed) {
                    setLoading(false);
                    return;
                }

                Swal.fire({
                    title: 'Preparing Download...',
                    html: 'Please wait while we prepare your files for download.',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                const zip = new JSZip();
                let successCount = 0;
                let failCount = 0;
                const errors: string[] = [];
                const usedFileNames = new Set<string>();

                const generateUniqueFileName = (baseFileName: string): string => {
                    const fileExtension = baseFileName.includes('.') 
                        ? baseFileName.substring(baseFileName.lastIndexOf('.'))
                        : '';
                    const fileNameWithoutExt = baseFileName.includes('.')
                        ? baseFileName.substring(0, baseFileName.lastIndexOf('.'))
                        : baseFileName;
                    
                    let uniqueFileName = baseFileName;
                    let counter = 0;
                    while (usedFileNames.has(uniqueFileName)) {
                        counter++;
                        uniqueFileName = `${fileNameWithoutExt} (${counter})${fileExtension}`;
                    }
                    usedFileNames.add(uniqueFileName);
                    return uniqueFileName;
                };

                await Promise.all(
                    documents.map(async (doc: any) => {
                        try {
                            const response = await fetch(doc.blobAddress, {
                                method: 'GET',
                                mode: 'cors',
                                credentials: 'omit',
                                cache: 'no-cache',
                            });
                            
                            if (!response.ok) {
                                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                            }
                            
                            const blob = await response.blob();
                            const uniqueFileName = generateUniqueFileName(doc.documentName || `document_${doc.stampedDocumentId}`);
                            zip.file(uniqueFileName, blob);
                            successCount++;
                        } catch (error: any) {
                            failCount++;
                            const errorMsg = error.message || 'Failed to download';
                            errors.push(`${doc.documentName}: ${errorMsg}`);
                            console.error(`Error downloading ${doc.documentName}:`, error);
                        }
                    })
                );

                if (successCount === 0) {
                    throw new Error('Failed to download any documents');
                }

                const zipBlob = await zip.generateAsync({ type: 'blob' });
                const url = window.URL.createObjectURL(zipBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${folderName}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                Swal.close();
                
                if (failCount > 0) {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Partial Success',
                        html: `
                            <p><strong>${successCount}</strong> document(s) downloaded successfully.</p>
                            <p><strong>${failCount}</strong> document(s) failed to download.</p>
                            ${errors.length > 0 ? `<p style="font-size: 12px; color: #666; margin-top: 10px;">Errors:<br>${errors.slice(0, 3).join('<br>')}${errors.length > 3 ? '<br>...' : ''}</p>` : ''}
                        `,
                        confirmButtonText: 'OK',
                    });
                } else {
                    Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: `All ${successCount} document(s) have been downloaded successfully.`,
                        confirmButtonText: 'OK',
                    });
                }
            }).catch((error) => {
                console.error('Error in download confirmation:', error);
            }).finally(() => {
                setLoading(false);
            });
        } catch (error: any) {
            console.error('Error in downloadStampedDocuments:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'An error occurred while downloading the documents.',
                confirmButtonText: 'OK',
            });
            setLoading(false);
        }
    };
    //#endregion

    const columns: GridColDef[] = [
        { field: 'message', headerName: 'Message', flex: 5, hideSortIcons: true,
            renderCell: (params) => {
                const category = params.row.categoryName;
                let exclamationCount = 0;
                
                if (category === CategoryNames.TagNotification) {
                    exclamationCount = 1;
                } else if (category === CategoryNames.SignatureRequest) {
                    exclamationCount = 2;
                }
                
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {exclamationCount > 0 && (
                            <Box sx={{ color: 'red', fontWeight: 'bold' }}>
                                {'!'.repeat(exclamationCount)}
                            </Box>
                        )}
                        {params.row.isRead ? <span>{params.value}</span> : <span><strong>{params.value}</strong></span>}
                    </Box>
                );
            },
            renderHeader: (params) => {
                const sortItem = sortModel?.find(
                    (item: any) => item.field === 'message'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )
            },
        },
        { field: 'categoryName', headerName: 'Category', flex: 1.5, hideSortIcons: true,
            renderCell: (params) => {
                const category = params.row.categoryName;
                const isRead = params.row.isRead;
                return isRead ? category : <strong>{category}</strong>;
            },
            renderHeader: (params) => {
                const sortItem = sortModel?.find(
                    (item: any) => item.field === 'categoryName'
                );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )
            },
        },
        { field: 'partnerName', headerName: 'Client', flex: 2, hideSortIcons: true, renderCell: (params) => {
            const client = params.row.partnerName;
            const isRead = params.row.isRead;
            return isRead ? <span>{client}</span> : <span><strong>{client}</strong></span>;
        },
        renderHeader: (params) => {
            const sortItem = sortModel?.find(
                (item: any) => item.field === 'partnerName'
            );
        
            const isSortedAsc = sortItem?.sort === 'asc';
            const isSortedDesc = sortItem?.sort === 'desc'
            return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )
        }, },
        { field: 'createdDate', headerName: 'Date', flex: 1.5, hideSortIcons: true,
            // align: 'right' 
            renderCell: (params) => {
                const createdDate = params.row.createdDate ? formatUtcToLocal(params.row.createdDate, 'MM-DD-YYYY hh:mm A') : '';
                const isRead = params.row.isRead;
                return isRead ? <span>{createdDate}</span> : <span><strong>{createdDate}</strong></span>;
            },
            renderHeader: (params) => {
                const sortItem = sortModel?.find(
                    (item: any) => item.field === 'createdDate'
                );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )
            },
        },
        { field: 'action', headerName: 'Action', flex: 1.5,sortable: false, hideSortIcons: true, minWidth: 100,
            filterable: false,
            disableColumnMenu: true,
            disableExport: true, 
            align: 'center', headerAlign: 'center',
            renderCell: (params) => {
                const isInboxItemLoading = Boolean(viewLoadingId === params.row.inboxId);
                const handleUploadDocuments = (e: any) => {
                    e?.stopPropagation?.();
                    e?.stopPropagation?.();
                    switch(params.row.categoryName) {
                        case CategoryNames.TagNotification:
                            setClientDocumentData({ reportTagId: params.row.reportTagId, files: [], inboxId: params.row.inboxId });
                            fetchReportTag(params.row.reportTagId);
                            setUploadReportTagDocumentsOpen(true);
                            break;
                        case CategoryNames.SignatureRequest:
                            if (params.row.signatureRequestId) {
                                setSelectedSignatureRequestIdForUpload(params.row.signatureRequestId);
                                setUploadSignatureRequestOpen(true);
                            }
                            if (params.row.signatureRequestId) {
                                setSelectedSignatureRequestIdForUpload(params.row.signatureRequestId);
                                setUploadSignatureRequestOpen(true);
                            }
                            break;
                        case CategoryNames.StampedDocuments:
                            break;
                        case CategoryNames.DiligentSearch:
                            break;
                        case CategoryNames.NonPremiumEndorsement:
                            break;
                        default:
                            break;
                    };
                };
                const handleViewDocuments = (e: any) => {
                    switch(params.row.categoryName) {
                        case CategoryNames.TagNotification:
                            e.stopPropagation();
                            if (params.row.reportTagId) {
                                openTagNotificationView(params.row.reportTagId, params.row.inboxId);
                            }
                            break;
                        case CategoryNames.SignatureRequest:
                            e.stopPropagation();
                            if (params.row.signatureRequestId) {
                                viewSignatureRequestDocuments(params.row.signatureRequestId, params.row.inboxId);
                            }
                            break;
                        case CategoryNames.StampedDocuments:
                            e.stopPropagation();
                            if (params.row.stampedDocumentDetailId) {
                                viewStampedDocuments(params.row.stampedDocumentDetailId, params.row.inboxId);
                            }
                            break;
                        case CategoryNames.DiligentSearch:
                            break;
                        case CategoryNames.NonPremiumEndorsement:
                            e.stopPropagation();
                            openNonPremiumRejectionView(params.row.inboxId);
                            break;
                        default:
                            break;
                    };
                };
                const handleDownloadDocuments = (e: any) => {
                    switch(params.row.categoryName) {
                        case CategoryNames.TagNotification:
                            e.stopPropagation();
                            if (params.row.reportTagId) {
                                downloadReportTagDocuments(params.row.reportTagId);
                            }
                            break;
                        case CategoryNames.SignatureRequest:
                            e.stopPropagation();
                            if (params.row.signatureRequestId) {
                                downloadSignatureRequestDocuments(params.row.signatureRequestId);
                            }
                            break;
                        case CategoryNames.StampedDocuments:
                            e.stopPropagation();
                            if (params.row.stampedDocumentDetailId) {
                                downloadStampedDocuments(params.row.stampedDocumentDetailId);
                            }
                            break;
                        case CategoryNames.DiligentSearch:
                            break;
                        default:
                            break;
                    };
                };
                const isTagNotification = params.row.categoryName === CategoryNames.TagNotification;
                const tagInHouseReadOnly = isTagNotification && Boolean(params.row.clientInHouseCloseRequestedAt);
                const isSignatureRequest = params.row.categoryName === CategoryNames.SignatureRequest;
                const isStampedDocuments = params.row.categoryName === CategoryNames.StampedDocuments;
                const isDiligentSearch = params.row.categoryName === CategoryNames.DiligentSearch;
                const isNonPremiumEndorsement = params.row.categoryName === CategoryNames.NonPremiumEndorsement;

                const viewTooltipTitle = isSignatureRequest
                    ? 'View & Upload Documents Attached'
                    : (isTagNotification || isNonPremiumEndorsement)
                      ? 'View Message'
                      : 'View Documents Attached';
                const downloadTooltipTitle = isSignatureRequest
                    ? 'Download Documents'
                    : 'Download all Documents Attached';

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <Button
                            size="small"
                            style={{ minWidth: '0px' }}
                            id={`inbox-action-button-${params.row.inboxId}`}
                            aria-controls={actionMenuAnchorEl?.id === `inbox-action-button-${params.row.inboxId}` ? `inbox-action-menu-${params.row.inboxId}` : undefined}
                            aria-haspopup="true"
                            aria-expanded={actionMenuAnchorEl?.id === `inbox-action-button-${params.row.inboxId}` ? 'true' : undefined}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClickOpenInboxActionOptions(params.row.inboxId);
                            }}
                        >
                            <MoreVertOutlinedIcon fontSize="small" />
                        </Button>
                        <Menu
                            id={`inbox-action-menu-${params.row.inboxId}`}
                            aria-labelledby={`inbox-action-button-${params.row.inboxId}`}
                            anchorEl={actionMenuAnchorEl}
                            open={Boolean(actionMenuAnchorEl && actionMenuAnchorEl.id === `inbox-action-button-${params.row.inboxId}`)}
                            onClose={handleCloseInboxActionOptions}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            MenuListProps={{ 'aria-labelledby': `inbox-action-button-${params.row.inboxId}` }}
                            sx={{ '& .MuiMenuItem-root': { fontSize: '14px', padding: '8px 16px' } }}
                        >
                            {(isTagNotification || isSignatureRequest) && (
                                <MenuItem
                                    disabled={tagInHouseReadOnly}
                                    onClick={(e) => {
                                        handleCloseInboxActionOptions();
                                        e.stopPropagation();
                                        handleUploadDocuments(e);
                                    }}
                                >
                                    <UploadFileOutlinedIcon fontSize="small" color="action" style={{ marginRight: 8 }} /> Upload Documents
                                </MenuItem>
                            )}
                            {(isTagNotification || isSignatureRequest || isStampedDocuments || isNonPremiumEndorsement) && (
                                <MenuItem
                                    disabled={isInboxItemLoading}
                                    onClick={(e) => {
                                        handleCloseInboxActionOptions();
                                        e.stopPropagation();
                                        handleViewDocuments(e);
                                    }}
                                >
                                    <RemoveRedEyeOutlinedIcon fontSize="small" color="action" style={{ marginRight: 8 }} /> {viewTooltipTitle}
                                </MenuItem>
                            )}
                            {(isTagNotification || isSignatureRequest || isStampedDocuments) && (
                                <MenuItem
                                    onClick={(e) => {
                                        handleCloseInboxActionOptions();
                                        e.stopPropagation();
                                        handleDownloadDocuments(e);
                                    }}
                                >
                                    <SimCardDownloadOutlinedIcon fontSize="small" color="action" style={{ marginRight: 8 }} /> {downloadTooltipTitle}
                                </MenuItem>
                            )}
                            {isDiligentSearch && (
                                <MenuItem
                                    onClick={(e) => {
                                        handleCloseInboxActionOptions();
                                        e.stopPropagation();
                                        handleCategoryClick(params.row);
                                    }}
                                >
                                    <OpenInNewOutlinedIcon fontSize="small" color="action" style={{ marginRight: 8 }} /> Go to {params.row.categoryName}
                                </MenuItem>
                            )}
                        </Menu>
                    </Box>
                );
            }
        }
    ];


    useEffect(() => {
        fetchUserPartners();
        // getIsDiligentSearchUser();
        fetchClientInboxCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchCustomerInbox();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, filterModel, sortModel, selectedTab, selectedCategory, selectedClient]);

    const fetchUserPartners = () => {
        setLoading(true);
        // const partnersUrl = '/partners/getPartners';
        const partnersUrl = '/customer/getPartnersList';
        apiClient(partnersUrl, {
            method: 'GET',
            headers: new Headers({
                Authorization: `Bearer ${token}`,
            }),
        }).then((responseJson) => {
            if (!responseJson) return;
            setPartners(responseJson.data as PartnerType[]);
            // check if the user is a venbrook customer
            setIsPartnersFilter(Boolean(responseJson.data.some(
                (partner: PartnerType) => partner.partnersId === VenbrookPartnersId
            )));
        }).catch(error => {
            console.error('Error fetching partners:', error);
        }).finally(() => {
            setLoading(false);
        });
    };
    const fetchClientInboxCategories = () => {
        setLoading(true);
        apiClient('/clientInbox/getInboxCategories', {
            method: 'GET',
            headers: new Headers({
                Authorization: `Bearer ${token}`,
            }),
        }).then((responseJson) => {
            if (!responseJson) return;
            const categories = (responseJson.data as CategoryModel[]).filter(
                (c) => c.categoryName !== 'Tag Extraction'
            );
            setClientInboxCategories(categories);
        }).catch(error => {
            console.error("Error fetching client inbox categories:", error);
        }).finally(() => {
            setLoading(false);
        });
    };
    const fetchCustomerInbox = () => {
        setLoading(true);
        
        // Convert filterModel to the format expected by the API
        const filterModelJson = filterModel && filterModel.items && filterModel.items.length > 0 ? filterModel : null;
        
        // Get sort field and direction from sortModel
        const sortField = sortModel && sortModel.length > 0 ? sortModel[0].field || '' : '';
        const sortDirection = sortModel && sortModel.length > 0 ? sortModel[0].sort || '' : '';
        
        // Determine isArchived based on selectedTab
        const isArchived = selectedTab === 'Archive' ? true : false;
        
        apiClient(`/clientInbox/getAllClientInbox`, {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }),
            body: JSON.stringify({
                pageSize: pageSize,
                pageNumber: currentPage,
                field: sortField,
                sort: sortDirection,
                filterModel: filterModelJson,
                isArchived: isArchived,
                clientInboxCategoryId: selectedCategory ? parseInt(selectedCategory) : 0,
                partnersId: typeof selectedClient === 'number' ? selectedClient : 0,
            }),
        }).then((responseJson) => {
            if (!responseJson) return;
            const { clientInbox, totalCount, totalPages, pageSize, pageNumber } = responseJson.data as ClientInboxResponse;
            
            // No need to sort or filter client-side - server handles it
            setCustomerInbox(Array.isArray(clientInbox) ? clientInbox : []);
            setTotalCount(Number.isFinite(totalCount) ? totalCount : 0);
            setIsLoading(false);
        }).catch((error) => {
            console.error('Error fetching customer inbox:', error);
            setCustomerInbox([]);
            setTotalCount(0);
        }).finally(() => {
            setLoading(false);
        });
    };

    const exportInboxToExcel = () => {
        const buildExportRows = (rows: CustomerInboxModel[]): (string | number)[][] => {
            const headers = ['Message', 'Category', 'Client', 'Date', 'Read', 'Archived', 'Error Types'];
            const dataRows = rows.map((row) => [
                row.message ?? '',
                row.categoryName ?? '',
                row.partnerName ?? '',
                row.createdDate ? formatUtcToLocal(row.createdDate, 'MM/DD/YYYY hh:mm A') : '',
                row.isRead ? 'Yes' : 'No',
                row.isArchived ? 'Yes' : 'No',
                row.errorTagList ?? '',
            ]);
            return [headers, ...dataRows];
        };

        const doExport = (data: CustomerInboxModel[]) => {
            if (!data || data.length === 0) {
                Swal.fire({ title: 'No data', text: 'No inbox messages to export.', icon: 'info', confirmButtonText: 'OK' });
                return;
            }
            const exportData = buildExportRows(data);
            const filename = `ClientInbox_${formatUtcToLocal(new Date().toISOString(), 'YYYY-MM-DD_HHmm')}.xlsx`;
            writeXlsxFile(exportData, filename);
        };

        if (selectedRows.length > 0) {
            doExport(selectedRows);
            return;
        }

        setLoading(true);
        const filterModelJson = filterModel?.items?.length ? filterModel : null;
        const sortField = sortModel?.[0]?.field ?? '';
        const sortDirection = sortModel?.[0]?.sort ?? '';
        const isArchived = selectedTab === 'Archive';
        const exportPageSize = Math.max(totalCount || 1, 1);
        apiClient(`/clientInbox/getAllClientInbox`, {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }),
            body: JSON.stringify({
                pageSize: exportPageSize,
                pageNumber: 1,
                field: sortField,
                sort: sortDirection,
                filterModel: filterModelJson,
                isArchived,
                clientInboxCategoryId: selectedCategory ? parseInt(selectedCategory, 10) : 0,
                partnersId: typeof selectedClient === 'number' ? selectedClient : 0,
            }),
        }).then((responseJson) => {
            if (!responseJson?.data?.clientInbox) {
                doExport(customerInbox);
                return;
            }
            doExport(responseJson.data.clientInbox as CustomerInboxModel[]);
        }).catch(() => {
            doExport(customerInbox);
        }).finally(() => {
            setLoading(false);
        });
    };

    const fetchReportTag = (reportTagId: number) => {
        setLoading(true);
        apiClient(`/ReportTags/getReportTagById/${reportTagId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            setReportTag(responseJson.data);
        }).catch(error => {
            console.error("Error fetching report tag:", error)
        }).finally(() => {
            setLoading(false);
        });
    };

    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarFilterButton className="me-3 sky-blue" />
                <GridToolbarColumnsButton className="me-3 sky-blue" />
                <GridToolbarDensitySelector className="me-3 sky-blue" defaultValue={'compact'} />
            </GridToolbarContainer>
        );
    };
    function EditToolbar() {
        return (
            <div className=" p-2 d-flex justify-content-end align-items-center datagrid-butons">
                <Tabs
                    value={selectedTab}
                    onChange={(_, newValue: "New Messages" | "Archive") => handleTabChange(newValue)}
                    sx={{
                        minHeight: 48,
                        "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.9375rem" },
                        "& .Mui-selected": { color: "#2099d2" },
                        "& .MuiTabs-indicator": {
                            backgroundColor: "#2099d2",
                            height: 3,
                            borderRadius: "3px 3px 0 0",
                        },
                    }}
                >
                    <Tab value="New Messages" label="New Messages" />
                    <Tab value="Archive" label="Archive" />
                </Tabs>
                <div className="ms-auto"><CustomToolbar /></div>
            </div>
        );
    };

    const ClientDropdown = () => (
        <FormControl size="small" fullWidth>
            <InputLabel id="client-label">Client</InputLabel>
            <Select
                label="Client"
                labelId="client-label"
                id="client"
                value={selectedClient === '' ? '' : selectedClient.toString()}
                onChange={handleClientChange}
                sx={{
                    marginTop: '-1px !important',
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
                    "& .MuiInputBase-root": { marginTop: '-3px !important' },
                    "& .MuiInputBase-input": { padding: '7px 14px !important' },
                }}
            >
                <MenuItem value=""><em>Client</em></MenuItem>
                {partners.map((partner, index) => (
                    <MenuItem key={index} value={partner.partnersId}>
                        {partner.customerName}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ width: '100%' }}>
            <style>
                {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                input::placeholder {
                    color: #333;
                    opacity: 1;
                }
                `}
            </style>
            <div style={{ position: "fixed", top: "0", left: "0", zIndex: 9990, width: "100%" }}>
                {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
            </div>

            {/* Top bar: title | Actions | Refresh | Export */}
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid #e2e8f0",
                    backgroundColor: "#fafbfc",
                    minHeight: 56,
                    mb: 1,
                    marginLeft: { xs: "-24px", md: "0px" },
                    marginRight: { xs: "-24px", md: "0px" },
                }}
            >
                <div className="d-flex flex-column me-auto">
                <h4 className="mb-0" style={{ fontWeight: 600, color: "#0f172a" }}>Inbox</h4>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 400, color: "#64748b" }}>
                    Select a category to view more details.
                </Typography>
                </div>
                
                <div className="d-flex gap-2 ms-auto">
                    <Button variant="contained" size="medium" onClick={markAsRead} startIcon={<RiEye2Line style={{ fontSize: '16px' }} />}
                        sx={{ textTransform: "none", fontWeight: 600, backgroundColor: "#2099d2", "&:hover": { backgroundColor: "#176990" } }}
                    >Mark as Read</Button>
                    <Button variant="contained" size="medium" onClick={markAsUnread} startIcon={<RiEyeCloseLine style={{ fontSize: '16px' }} />}
                        sx={{ textTransform: "none", fontWeight: 600, backgroundColor: "#2099d2", "&:hover": { backgroundColor: "#176990" } }}
                    >Mark as Unread</Button>
                    <Button variant="contained" size="medium" onClick={selectedTab === "New Messages" ? markAsArchived : markAsUnarchived} startIcon={<RiArchiveLine style={{ fontSize: '16px' }} />}
                        sx={{ textTransform: "none", fontWeight: 600, backgroundColor: "#2099d2", "&:hover": { backgroundColor: "#176990" } }}
                    >{selectedTab === "New Messages" ? "Move to Archive" : "Move to New Messages"}</Button>
                </div>
                <Button
                    variant="outlined"
                    size="medium"
                    onClick={() => fetchCustomerInbox()}
                    startIcon={<Refresh />}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: "#e2e8f0",
                        color: "#475569",
                        "&:hover": { borderColor: "#2099d2", backgroundColor: "#2099d214", color: "#2099d2" },
                    }}
                >
                    Refresh
                </Button>
                <Button
                    variant="outlined"
                    size="medium"
                    onClick={exportInboxToExcel}
                    disabled={loading}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: "#e2e8f0",
                        color: "#475569",
                        "&:hover": { borderColor: "#2099d2", backgroundColor: "#2099d214", color: "#2099d2" },
                    }}
                >
                    Export
                </Button>
            </Box>

            {!isDiligentSearchOnlyUser && (
                <Box sx={{ width: "100%", py: 2, px: 2, backgroundColor: "#ffffff", borderBottom: "1px solid #e2e8f0" }}>
                    <Grid container spacing={2} display="flex">
                        <Grid item xl={2} lg={4} sm={6} xs={12}>
                            <FormControl size="small" fullWidth>
                                <InputLabel id="category-label">Category</InputLabel>
                                <Select
                                    labelId="category-label"
                                    id="category"
                                    value={selectedCategory}
                                    label="Category"
                                    onChange={handleCategoryChange}
                                    sx={{
                                        marginTop: '-1px !important',
                                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
                                        "& .MuiInputBase-root": { marginTop: '-3px !important' },
                                        "& .MuiInputBase-input": { padding: '7px 14px !important' },
                                    }}
                                >
                                    <MenuItem value=""><em>Category</em></MenuItem>
                                    {clientInboxCategories.map((category) => (
                                        <MenuItem key={category.clientInboxCategoryId} value={category.clientInboxCategoryId}>
                                            {category.categoryName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {isPartnersFilter && (
                            <Grid item xl={2} lg={4} sm={6} xs={12}>
                                <ClientDropdown />
                            </Grid>
                        )}
                        <Grid item xs={12} sm="auto">
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    onClick={handleClear}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        borderColor: "#e2e8f0",
                                        color: "#475569",
                                        "&:hover": { borderColor: "#2099d2", color: "#2099d2" },
                                    }}
                                >
                                    Clear
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            )}
            
            {/* Grid wrapper (match CustomerTransactions) */}
            <Box
                sx={{
                    height: { xs: "calc(100vh - 250px)", sm: "calc(100vh - 250px)" },
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)",
                    border: "1px solid #e2e8f0",
                    backgroundColor: "#ffffff",
                    position: "relative",
                }}
            >
                {/* {loading && <LinearProgress sx={{ backgroundColor: "#2099d2" }} />} */}
                {(isLoading || (loading && viewLoadingId === null)) && (<>
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            backgroundColor: "rgba(250, 251, 252, 0.85)",
                            zIndex: 10,
                            border: "1px solid #e2e8f0",
                            borderRadius: 1,
                        }}
                    >
                        <Box sx={{ width: "100%", maxWidth: 320 }}>
                            <LinearProgress color="info" sx={{ backgroundColor: "#e2e8f0", "& .MuiLinearProgress-bar": { backgroundColor: "#132D3D" } }} />
                            <Box sx={{ mt: 1.5, textAlign: "center" }}>
                                <span style={{ fontSize: "14px", fontWeight: 600, color: "#475569" }}>Loading...</span>
                            </Box>
                        </Box>
                    </Box>
                </>) }
                <Box sx={{ flex: "1 1 0%", overflow: "auto", minHeight: 0 }}>
                    <DataGridPro
                        rows={customerInbox}
                        columns={columns}
                        getRowId={(r) => r.inboxId}
                        rowCount={totalCount}
                        page={currentPage - 1}
                        pageSize={pageSize}
                        pagination
                        paginationMode="server"
                        onPageChange={(newPage: number) => setCurrentPage(newPage + 1)}
                        rowsPerPageOptions={[25, 50, 100, 250, 500, 1000]}
                        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                        filterMode="server"
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterModelChange}
                        sortingMode="server"
                        sortingOrder={["asc", "desc"]}
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        disableSelectionOnClick
                        loading={isLoading || (loading && viewLoadingId === null)}
                        checkboxSelection
                        selectionModel={selectedRows.map((row) => row.inboxId).filter((id): id is number => id !== undefined)}
                        onSelectionModelChange={(newSelectionModel) => {
                            const selectedObjects = (newSelectionModel as number[])
                                .map((id) => (customerInbox || []).find((row) => row?.inboxId === id))
                                .filter((row): row is CustomerInboxModel => row !== undefined);
                            setSelectedRows(selectedObjects);
                        }}
                        components={{
                            Toolbar: EditToolbar,
                            NoRowsOverlay: () => (
                                <Typography variant="body1" sx={{ textAlign: "center", padding: "20px" }}>
                                    No inbox messages found
                                </Typography>
                            ),
                        }}
                        sx={{
                            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            border: "none",
                            fontSize: "14px",
                            color: "#475569",
                            backgroundColor: "#ffffff",
                            "& .MuiDataGridPro-columnHeaderTitle": {
                                whiteSpace: "normal",
                                lineHeight: 1.35,
                                fontWeight: 600,
                                fontSize: "12px",
                                letterSpacing: "0.05em",
                                textTransform: "uppercase",
                                color: "#64748b",
                            },
                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "#f8fafc",
                                borderBottom: "2px solid #e2e8f0",
                                borderTop: "2px solid #e2e8f0",
                                minHeight: "52px !important",
                                maxHeight: "52px !important",
                            },
                            "& .MuiDataGrid-columnHeader": { "&:focus, &:focus-within": { outline: "none" } },
                            "& .MuiDataGrid-row": {
                                transition: "background-color 0.15s ease",
                                "&:nth-of-type(even)": { backgroundColor: "#fafbfc" },
                                "&:hover": { backgroundColor: "#f1f5f9 !important" },
                            },
                            "& .MuiDataGrid-cell": {
                                borderBottom: "1px solid #f1f5f9",
                                fontSize: "14px",
                                color: "#334155",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                "&:focus": { outline: "none" },
                            },
                            "& .MuiDataGrid-virtualScroller": { border: "none", marginTop: "50px !important" },
                            "& .MuiDataGrid-main": { border: "none" },
                            "& .MuiDataGrid-footerContainer": {
                                borderTop: "1px solid #e2e8f0",
                                backgroundColor: "#f8fafc",
                                minHeight: 56,
                            },
                            "& .MuiTablePagination-root": { color: "#64748b", fontSize: "13px" },
                            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { marginBottom: 0 },
                            "& .MuiTablePagination-select": {
                                fontSize: "13px",
                                borderRadius: "6px",
                                border: "1px solid #e2e8f0",
                            },
                            "& .MuiIconButton-root": {
                                color: "#64748b",
                                "&:hover": { backgroundColor: "#f1f5f9", color: "#475569" },
                            },
                            "& .MuiDataGrid-cell[data-field='dateCreated']": { paddingRight: "36px" },
                            "& .MuiDataGrid-columnHeader[data-field='dateCreated']": { paddingRight: "36px" },
                        }}
                    />
                </Box>
            </Box>

            {/* Signature Request – Message Center layout matching Stamped Document inbox */}
            <Dialog
                open={signatureRequestDialog.open}
                onClose={handleCloseSignatureRequestInfo}
                fullWidth
                maxWidth="xl"
                PaperProps={{ style: { width: '98%', maxWidth: 1600, maxHeight: '90vh', margin: 'auto' } }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#e8f5fe' }}>
                    <DialogTitle style={{ color: '#1976D2', fontSize: '25px' }}>INBOX</DialogTitle>
                </Box>
                <DialogContent style={{ backgroundColor: '#f4f9fc', padding: '24px 24px 16px' }}>
                    <div style={{ display: 'flex', gap: '20px', flex: 1, minHeight: '400px', alignItems: 'stretch' }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <h3 style={{ color: '#00A9E0', marginBottom: 12, marginTop: 2, marginLeft: 0, fontWeight: 'bold', fontSize: '20px', lineHeight: 1.3 }}>MESSAGE</h3>
                            <div style={{ flex: 1, minHeight: 360, overflowY: 'auto', border: '1px solid #ddd', padding: '16px', borderRadius: '4px', backgroundColor: '#fff' }}>
                                {signatureRequestDialog.content && (
                                    <>
                                        <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                                            Hello! Please review the attached document and obtain the licensee&apos;s signature at your earliest convenience. Once signed, you can return it to our team by uploading it to this message in your Client Inbox.
                                        </Typography>
                                        <Box component="dl" sx={{ m: 0, '& dd': { m: 0, ml: 2 }, '& dt': { fontWeight: 600, mt: 1.5, color: 'text.secondary' } }}>
                                            <Typography component="dt">Licensee:</Typography>
                                            <Typography component="dd">{signatureRequestDialog.content.licensee}</Typography>
                                            <Typography component="dt">State:</Typography>
                                            <Typography component="dd">{signatureRequestDialog.content.state}</Typography>
                                            <Typography component="dt">Name of Form:</Typography>
                                            <Typography component="dd">{signatureRequestDialog.content.nameOfForm}</Typography>
                                            <Typography component="dt">Due Date:</Typography>
                                            <Typography component="dd">{signatureRequestDialog.content.dueDate ? formatUtcToLocal(signatureRequestDialog.content.dueDate, 'MM/DD/YYYY') : ''}</Typography>
                                            <Typography component="dt">Signature Type:</Typography>
                                            <Typography component="dd">{signatureRequestDialog.content.signatureType}</Typography>
                                            <Typography component="dt">Notarization Required:</Typography>
                                            <Typography component="dd">{signatureRequestDialog.content.notarizationRequired}</Typography>
                                        </Box>
                                    </>
                                )}
                            </div>
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <div style={{ marginBottom: 12, minHeight: 28, marginLeft: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3 style={{ color: '#00A9E0', fontWeight: 'bold', fontSize: '20px', margin: 0, lineHeight: 1.3 }}>ATTACHMENTS</h3>
                                <Button
                                    variant="contained"
                                    startIcon={<CloudUploadOutlinedIcon />}
                                    onClick={handleUploadSignatureRequestFromInfo}
                                    sx={{ backgroundColor: '#1976D2', color: '#fff', textTransform: 'none', fontWeight: 600 }}
                                >
                                    Upload Document
                                </Button>
                            </div>
                            <div style={{ flex: 1, minHeight: 360, display: 'flex', flexDirection: 'column', border: '1px solid #ddd', padding: '16px', borderRadius: '4px', backgroundColor: '#fff' }}>
                                <h3 style={{ color: '#000', fontWeight: 'bold', fontSize: '16px', marginBottom: 12, marginLeft: 0 }}>History</h3>
                                <div style={{ flex: 1, minHeight: 0, width: '100%' }}>
                                    {signatureRequestDialog.documents.length > 0 ? (
                                        <DataGridPro
                                            autoHeight
                                            rows={signatureRequestDialog.documents}
                                            getRowId={(r) => String(r?.id ?? r?.documentUrl ?? '')}
                                            columns={[
                                                {
                                                    field: 'documentName',
                                                    headerName: 'File Name',
                                                    flex: 2,
                                                    minWidth: 180,
                                                    sortable: false,
                                                    renderCell: (params) => (
                                                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                                            {params.value || 'Document'}
                                                        </Typography>
                                                    ),
                                                },
                                                {
                                                    field: 'sentBy',
                                                    headerName: 'Uploaded By',
                                                    flex: 1,
                                                    minWidth: 120,
                                                    valueGetter: (params: any) => {
                                                        const miscRoles = ['Super Admin', 'MISC Filer', 'MISC State Reporter'];
                                                        const roles = (params.row.createdByUserRole ?? '').split(',').map((r: string) => r.trim());
                                                        const hasMiscRole = roles.some((r: string) => miscRoles.includes(r));
                                                        return hasMiscRole ? 'MISC team' : (params.row.sentBy ?? 'N/A');
                                                    },
                                                },
                                                {
                                                    field: 'createdDate',
                                                    headerName: 'Upload Date',
                                                    flex: 1,
                                                    minWidth: 130,
                                                    renderCell: (params) => formatUtcToLocal(params.value, 'MM/DD/YYYY HH:mm'),
                                                },
                                                {
                                                    field: 'action',
                                                    headerName: 'Actions',
                                                    flex: 0.6,
                                                    minWidth: 90,
                                                    sortable: false,
                                                    renderCell: (params) => (
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Tooltip title="View" arrow>
                                                                <IconButton
                                                                    size="small"
                                                                    sx={{ color: LightBlueBtnColor }}
                                                                    onClick={() => {
                                                                        if (params.row.documentUrl && params.row.documentName) {
                                                                            setDocumentsToView([{ documentUrl: params.row.documentUrl, documentName: params.row.documentName }]);
                                                                            setCurrentDocumentIndex(0);
                                                                            setDocumentViewerOpen(true);
                                                                        }
                                                                    }}
                                                                >
                                                                    <FaRegEye />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Download" arrow>
                                                                <IconButton
                                                                    size="small"
                                                                    sx={{ color: LightBlueBtnColor }}
                                                                    onClick={() => params.row.documentUrl && params.row.documentName && downloadSingleDocument(params.row.documentUrl, params.row.documentName)}
                                                                >
                                                                    <FaDownload />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    ),
                                                },
                                            ]}
                                            hideFooter
                                            disableSelectionOnClick
                                            sx={{
                                                border: 0,
                                                '& .MuiDataGrid-columnHeaders': {
                                                    backgroundColor: '#fff',
                                                    borderBottom: '1px solid #e2e8f0',
                                                    color: '#000',
                                                },
                                                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, color: '#000' },
                                                '& .MuiDataGrid-cell': { borderBottom: '1px solid #e2e8f0' },
                                            }}
                                            components={{ NoRowsOverlay: () => <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>No documents available</Typography> }}
                                        />
                                    ) : (
                                        <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>No documents available</Typography>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                        <Button onClick={handleCloseSignatureRequestInfo} variant="outlined" sx={{ backgroundColor: '#E0E0E0', color: '#000', borderColor: '#ccc' }}>Cancel</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Tag Notification – Message Center layout (matches MessageDialog structure) */}
            <Dialog
                open={tagNotificationDialog.open}
                onClose={handleCloseTagNotificationInfo}
                fullWidth
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: '95%',
                        maxHeight: '95vh',
                        height: 'auto',
                        margin: 'auto',
                        borderRadius: 2,
                        boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    },
                }}
                sx={{ '& .MuiDialog-paper': { overflow: 'hidden' } }}
            >
                <DialogTitle
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        py: 2,
                        px: 3,
                        background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
                        color: '#fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <LocalOfferOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
                        <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                            Inbox
                        </Typography>
                        <Typography component="span" variant="body1" sx={{ opacity: 0.95, ml: 1 }}>
                            Tag Notification
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleCloseTagNotificationInfo}
                        sx={{
                            color: 'rgba(255,255,255,0.9)',
                            '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' },
                        }}
                        aria-label="Close dialog"
                    >
                        <CloseOutlinedIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: 'grey.50', px: { xs: 2, sm: 3 }, py: 3, flex: '1 1 auto', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', gap: 3, flex: '1 1 auto', minHeight: 0, py: 2, overflow: 'auto' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, fontSize: '1rem', letterSpacing: '0.02em', display: 'block' }}>Message</Typography>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'grey.50', overflow: 'auto', minHeight: 200, maxHeight: { xs: 360, md: '50vh' } }}>
                                {tagNotificationDialog.reportTag && (
                                    <>
                                        <DialogContentText sx={{ textAlign: 'left', mb: 0.5 }}><strong>Report a Tag — categories</strong></DialogContentText>
                                        <List dense disablePadding sx={{ mb: 2, listStyle: 'none', pl: 0, ml: 0 }}>
                                            {tagNotificationDialog.reportTag.errorTagCodeIds?.map((id: number) => {
                                                const code = tagNotificationDialog.errorTagCodes.find((c: ErrorTagCodeModel) => c.errorTagCodeId === id);
                                                return code ? (
                                                    <ListItem key={id} disablePadding sx={{ py: 0.25, pl: 0, ml: 0 }}>
                                                        <Chip label={`${code.errorTagCodeId}: ${code.errorTagName}`} color="primary" variant="outlined" size="small" />
                                                    </ListItem>
                                                ) : null;
                                            })}
                                        </List>
                                        <DialogContentText sx={{ textAlign: 'left', my: 0.5, mb: 1 }}><strong>Number of Tags :</strong> <Box component="span" sx={{ color: 'text.primary' }}>{tagNotificationDialog.reportTag.isMultipleTags ? 'Multiple' : 'Single'}</Box></DialogContentText>
                                        <Box sx={{ textAlign: 'left', mb: 2 }}>
                                            <DialogContentText sx={{ textAlign: 'left', my: 0.5 }}><strong>Client :</strong> <Box component="span" sx={{ color: 'text.primary' }}>{tagNotificationDialog.reportTag.agency ?? ''}</Box></DialogContentText>
                                            {tagNotificationDialog.reportTag.dueDate != null && String(tagNotificationDialog.reportTag.dueDate).trim() !== '' && (
                                                <DialogContentText sx={{ textAlign: 'left', my: 0.5 }}>
                                                    <strong>Due Date :</strong>{' '}
                                                    <Box component="span" sx={{ color: 'text.primary' }}>
                                                        {formatUtcToLocal(tagNotificationDialog.reportTag.dueDate, 'MM/DD/YYYY', true)}
                                                    </Box>
                                                </DialogContentText>
                                            )}
                                            {!tagNotificationDialog.reportTag.isMultipleTags && (<>
                                                <DialogContentText sx={{ textAlign: 'left', my: 0.5 }}><strong>Invoice Number :</strong> <Box component="span" sx={{ color: 'text.primary' }}>{tagNotificationDialog.reportTag.invoiceNumber ?? ''}</Box></DialogContentText>
                                                <DialogContentText sx={{ textAlign: 'left', my: 0.5 }}><strong>Policy Number :</strong> <Box component="span" sx={{ color: 'text.primary' }}>{tagNotificationDialog.reportTag.policyNumber ?? ''}</Box></DialogContentText>
                                                <DialogContentText sx={{ textAlign: 'left', my: 0.5 }}><strong>Insured Name :</strong> <Box component="span" sx={{ color: 'text.primary' }}>{tagNotificationDialog.reportTag.insuredName ?? ''}</Box></DialogContentText>
                                                <DialogContentText sx={{ textAlign: 'left', my: 0.5 }}><strong>Filer Notes :</strong> <Box component="span" sx={{ color: 'text.primary' }}>{tagNotificationDialog.reportTag.filerNotes ?? ''}</Box></DialogContentText>
                                            </>)}
                                        </Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>Messages</Typography>
                                    </>
                                )}
                                {tagNotificationDialog.filerNotes.length > 0 ? (
                                    tagNotificationDialog.filerNotes.map((item, index) => {
                                        const miscRoles = ['Super Admin', 'MISC Filer', 'MISC State Reporter'];
                                        const roles = item.sentByUserRole?.split(',').map((r: string) => r.trim()) ?? [];
                                        const hasMiscRole = roles.some((r: string) => miscRoles.includes(r));
                                        const displaySentBy = hasMiscRole ? 'MISC team' : (item.sentBy ?? '');
                                        return (
                                            <Box key={index} sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1.5, mb: 2, border: '1px solid', borderColor: 'divider' }}>
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                    {[displaySentBy, item.sentOn && formatUtcToLocal(item.sentOn, 'MM/DD/YYYY hh:mm A')].filter(Boolean).join(' • ')}
                                                </Typography>
                                                <InboxMessageBody text={item.filerNote} />
                                            </Box>
                                        );
                                    })
                                ) : (
                                    <Typography sx={{ color: 'text.secondary' }}>No messages available</Typography>
                                )}
                            </Paper>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, fontSize: '1rem', letterSpacing: '0.02em', display: 'block' }}>Attachments</Typography>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'grey.50', overflow: 'auto', minHeight: 200, maxHeight: { xs: 360, md: '50vh' } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                    <Typography variant="overline" sx={{ color: 'text.primary', fontWeight: 700, fontSize: '1rem', letterSpacing: '0.02em', display: 'block', mb: 1.5 }}>History</Typography>
                                    {!tagNotificationDialog.reportTag?.clientInHouseCloseRequestedAt ? (
                                        <Button
                                            variant="contained"
                                            size="medium"
                                            startIcon={<CloudUploadOutlinedIcon />}
                                            onClick={handleUploadDocumentFromTagNotificationInfo}
                                            sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#2099d2', '&:hover': { backgroundColor: '#176990' } }}
                                        >
                                            Upload Document
                                        </Button>
                                    ) : null}
                                </Box>
                                {tagNotificationDialog.documents.length > 0 ? (
                                    <Box sx={{ width: '100%', mb: 2 }}>
                                        <DataGridPro
                                            rows={tagNotificationDialog.documents}
                                            getRowId={(r) => String(r?.id ?? r?.documentUrl ?? '')}
                                            autoHeight
                                            columns={[
                                                { field: 'documentName', headerName: 'File Name', flex: 2, minWidth: 180, sortable: false, renderCell: (params) => <Typography variant="body2" sx={{ color: 'text.primary' }}>{params.value || 'Document'}</Typography> },
                                                { field: 'sentBy', headerName: 'Uploaded By', flex: 1, minWidth: 120, valueGetter: (params) => { const miscRoles = ['Super Admin', 'MISC Filer', 'MISC State Reporter']; const roles = (params.row.createdByUserRole ?? '').split(',').map((r: string) => r.trim()); const hasMiscRole = roles.some((r: string) => miscRoles.includes(r)); return hasMiscRole ? 'MISC team' : (params.row.sentBy ?? 'N/A'); } },
                                                { field: 'createdDate', headerName: 'Upload Date', flex: 1, minWidth: 130, renderCell: (params) => formatUtcToLocal(params.value, 'MM/DD/YYYY HH:mm') },
                                                { field: 'action', headerName: 'Actions', flex: 0.6, minWidth: 90, sortable: false, renderCell: (params) => (
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Tooltip title="View" arrow><IconButton size="small" sx={{ color: LightBlueBtnColor }} onClick={() => { if (params.row.documentUrl && params.row.documentName) { setDocumentsToView([{ documentUrl: params.row.documentUrl, documentName: params.row.documentName }]); setCurrentDocumentIndex(0); setDocumentViewerOpen(true); } }}><FaRegEye /></IconButton></Tooltip>
                                                        <Tooltip title="Download" arrow><IconButton size="small" sx={{ color: LightBlueBtnColor }} onClick={() => params.row.documentUrl && params.row.documentName && downloadSingleDocument(params.row.documentUrl, params.row.documentName)}><FaDownload /></IconButton></Tooltip>
                                                    </Box>
                                                ) },
                                            ]}
                                            pagination
                                            pageSize={5}
                                            rowsPerPageOptions={[5]}
                                            disableSelectionOnClick
                                            disableColumnMenu
                                            sx={{
                                                borderRadius: '10px',
                                                overflow: 'hidden',
                                                '& .MuiDataGrid-main': { overflow: 'hidden' },
                                                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0', minHeight: '52px !important', maxHeight: '52px !important' },
                                                '& .MuiDataGrid-columnHeader': { '&:focus, &:focus-within': { outline: 'none' } },
                                                '& .MuiDataGrid-row': { transition: 'background-color 0.15s ease', '&:nth-of-type(even)': { backgroundColor: '#fafbfc' }, '&:hover': { backgroundColor: '#f1f5f9 !important' } },
                                                '& .MuiDataGrid-cell': { borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#334155', overflow: 'visible', whiteSpace: 'normal', lineHeight: 'normal', wordWrap: 'break-word', textOverflow: 'clip', padding: '8px 16px', '&:focus': { outline: 'none' } },
                                                '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc', minHeight: 56 },
                                                '& .MuiTablePagination-root': { color: '#64748b', fontSize: '13px' },
                                                '& .MuiTablePagination-select': { fontSize: '13px', borderRadius: '6px', border: '1px solid #e2e8f0' },
                                                '& .MuiIconButton-root': { color: '#64748b', '&:hover': { backgroundColor: '#f1f5f9', color: '#475569' } },
                                            }}
                                            components={{ NoRowsOverlay: () => <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>No documents available</Typography> }}
                                        />
                                    </Box>
                                ) : (
                                    <Box sx={{ width: '100%', textAlign: 'center', py: 4, color: '#666', fontSize: '16px' }}>No documents available</Box>
                                )}
                            </Paper>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        borderTop: 1,
                        borderColor: 'divider',
                        backgroundColor: 'grey.100',
                        px: 3,
                        py: 2,
                        gap: 1.5,
                        flexShrink: 0,
                    }}
                >
                        {!tagNotificationDialog.reportTag?.clientInHouseCloseRequestedAt ? (
                            <>
                            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, fontSize: '1rem', letterSpacing: '0.02em', display: 'block' }}>Reply</Typography>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                        maxRows={8}
                                placeholder="Type your reply here..."
                                value={tagNotificationReplyToMisc}
                                onChange={(e) => setTagNotificationReplyToMisc(e.target.value)}
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px !important' },
                                    '& .MuiInputBase-input': { padding: '10px 14px !important' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' },
                                }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    onClick={handleCloseTagNotificationInfo}
                                    startIcon={<CloseOutlinedIcon />}
                                    sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#2099d2', backgroundColor: '#2099d214', color: '#2099d2' } }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    size="medium"
                                    disabled={!tagNotificationReplyToMisc?.trim()}
                                    onClick={handleReplyToMiscFromTag}
                                    startIcon={<SendOutlinedIcon />}
                                    sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#2099d2', '&:hover': { backgroundColor: '#176990' }, px: 2.5 }}
                                >
                                    Send to MISC
                                </Button>
                            </Box>
                            </>
                        ) : null}
                </DialogActions>
            </Dialog>

            {/* Single Attached Documents dialog (Tag Notification, Signature Request, etc.) */}
            <Dialog open={attachedDocumentsOpen} onClose={handleCloseAttachedDocuments} maxWidth="lg" fullWidth>
                <DialogTitle className="card-title">Attached Documents</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <div style={{ height: '50vh', width: '100%' }}>
                            <DataGridPro
                                rows={attachedDocumentsList}
                                getRowId={(r) => r?.id ?? r?.documentUrl ?? ''}
                                columns={[
                                    { field: 'documentName', headerName: 'Document Name', flex: 3, minWidth: 200 },
                                    { field: 'sentBy', headerName: 'Sent By', flex: 1.5, minWidth: 120 },
                                    {
                                        field: 'createdDate',
                                        headerName: 'Date Uploaded',
                                        flex: 1,
                                        minWidth: 120,
                                        renderCell: (params) => formatUtcToLocal(params.value, 'MM/DD/YYYY'),
                                    },
                                    {
                                        field: 'action',
                                        headerName: 'Actions',
                                        flex: 1,
                                        minWidth: 120,
                                        align: 'center',
                                        headerAlign: 'center',
                                        renderCell: (params) => (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                                                <Tooltip title="View Document" arrow>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: LightBlueBtnColor }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (params.row.documentUrl && params.row.documentName) {
                                                                viewDocumentFromList(params.row.documentUrl, params.row.documentName);
                                                            }
                                                        }}
                                                    >
                                                        <FaRegEye />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Download Document" arrow>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: LightBlueBtnColor }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (params.row.documentUrl && params.row.documentName) {
                                                                downloadSingleDocument(params.row.documentUrl, params.row.documentName);
                                                            }
                                                        }}
                                                    >
                                                        <FaDownload />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        ),
                                    },
                                ]}
                                sx={{ border: 0 }}
                                hideFooter
                                disableSelectionOnClick
                            />
                        </div>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <button className="btn-2 btn d-flex btn-primary search-btn" type="button" onClick={handleCloseAttachedDocuments}>
                        Close
                    </button>
                </DialogActions>
            </Dialog>

            {/* Stamped Document – Message Center layout (no Reply, no Send & Return, no Upload) */}
            <Dialog
                open={stampedDocumentInfoOpen}
                onClose={handleCloseStampedDocumentInfo}
                fullWidth
                maxWidth="xl"
                PaperProps={{ style: { width: '98%', maxWidth: 1600, maxHeight: '90vh', margin: 'auto' } }}
            >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#e8f5fe' }}>
                    <DialogTitle style={{ color: '#1976D2', fontSize: '25px' }}>INBOX</DialogTitle>
                </Box>
                <DialogContent style={{ backgroundColor: '#f4f9fc', padding: '24px 24px 16px' }}>
                    <div style={{ display: 'flex', gap: '20px', flex: 1, minHeight: '400px', alignItems: 'stretch' }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <h3 style={{ color: '#00A9E0', marginBottom: 12, marginTop: 2, marginLeft: 0, fontWeight: 'bold', fontSize: '20px', lineHeight: 1.3 }}>MESSAGE</h3>
                            <div style={{ flex: 1, minHeight: 360, overflowY: 'auto', border: '1px solid #ddd', padding: '16px', borderRadius: '4px', backgroundColor: '#fff' }}>
                                {stampedDocumentInfoContent && (
                                    <>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {stampedDocumentInfoContent.message}
                                        </Typography>
                                        <Link href={stampedDocumentInfoContent.linkUrl} target="_blank" rel="noopener noreferrer" sx={{ color: PrimaryBlueColor, wordBreak: 'break-all' }}>
                                            {stampedDocumentInfoContent.linkUrl}
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <div style={{ marginBottom: 12, minHeight: 28, marginLeft: 0 }}>
                                <h3 style={{ color: '#00A9E0', fontWeight: 'bold', fontSize: '20px', margin: 0, lineHeight: 1.3 }}>ATTACHMENTS</h3>
                            </div>
                            <div style={{ flex: 1, minHeight: 360, display: 'flex', flexDirection: 'column', border: '1px solid #ddd', padding: '16px', borderRadius: '4px', backgroundColor: '#fff' }}>
                                <h3 style={{ color: '#000', fontWeight: 'bold', fontSize: '16px', marginBottom: 12, marginLeft: 0 }}>History</h3>
                                <div style={{ flex: 1, minHeight: 0, width: '100%' }}>
                                {pendingStampedDocuments.length > 0 ? (
                                    <DataGridPro
                                        rows={pendingStampedDocuments}
                                        getRowId={(row: { documentUrl: string; documentName: string }) => row.documentUrl || row.documentName || `doc-${row.documentName}`}
                                        columns={[
                                            { field: 'documentName', headerName: 'Document Name', flex: 2, minWidth: 180 },
                                            { field: 'action', headerName: 'Actions', flex: 0.6, minWidth: 100, sortable: false, renderCell: (params) => (
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="View" arrow><IconButton size="small" sx={{ color: LightBlueBtnColor }} onClick={() => { setDocumentsToView([{ documentUrl: params.row.documentUrl, documentName: params.row.documentName }]); setCurrentDocumentIndex(0); setDocumentViewerOpen(true); }}><FaRegEye /></IconButton></Tooltip>
                                                    <Tooltip title="Download" arrow><IconButton size="small" sx={{ color: LightBlueBtnColor }} onClick={() => downloadSingleDocument(params.row.documentUrl, params.row.documentName)}><FaDownload /></IconButton></Tooltip>
                                                </Box>
                                            ) }
                                        ]}
                                        hideFooter
                                        disableSelectionOnClick
                                        sx={{ border: 0 }}
                                    />
                                ) : (
                                    <Typography sx={{ color: '#666', textAlign: 'center', py: 3 }}>No documents available</Typography>
                                )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                        <Button onClick={handleCloseStampedDocumentInfo} variant="outlined" sx={{ backgroundColor: '#E0E0E0', color: '#000', borderColor: '#ccc' }}>Cancel</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Non-Premium Endorsement rejection – read-only INBOX (MESSAGE + ATTACHMENTS, no upload) */}
            <Dialog
                open={nonPremiumRejectionDialog.open}
                onClose={handleCloseNonPremiumRejectionInfo}
                fullWidth
                maxWidth="xl"
                PaperProps={{ style: { width: '98%', maxWidth: 1600, maxHeight: '90vh', margin: 'auto' } }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#e8f5fe' }}>
                    <DialogTitle style={{ color: '#1976D2', fontSize: '25px' }}>INBOX</DialogTitle>
                </Box>
                <DialogContent style={{ backgroundColor: '#f4f9fc', padding: '24px 24px 16px' }}>
                    <div style={{ display: 'flex', gap: '20px', flex: 1, minHeight: '400px', alignItems: 'stretch' }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <h3 style={{ color: '#00A9E0', marginBottom: 12, marginTop: 2, marginLeft: 0, fontWeight: 'bold', fontSize: '20px', lineHeight: 1.3 }}>MESSAGE</h3>
                            <div style={{ flex: 1, minHeight: 360, overflowY: 'auto', border: '1px solid #ddd', padding: '16px', borderRadius: '4px', backgroundColor: '#fff' }}>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                    {nonPremiumRejectionDialog.messageText}
                                </Typography>
                            </div>
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <div style={{ marginBottom: 12, minHeight: 28, marginLeft: 0 }}>
                                <h3 style={{ color: '#00A9E0', fontWeight: 'bold', fontSize: '20px', margin: 0, lineHeight: 1.3 }}>ATTACHMENTS</h3>
                            </div>
                            <div style={{ flex: 1, minHeight: 360, display: 'flex', flexDirection: 'column', border: '1px solid #ddd', padding: '16px', borderRadius: '4px', backgroundColor: '#fff' }}>
                                <h3 style={{ color: '#000', fontWeight: 'bold', fontSize: '16px', marginBottom: 12, marginLeft: 0 }}>History</h3>
                                <div style={{ flex: 1, minHeight: 0, width: '100%' }}>
                                    {nonPremiumRejectionDialog.documents.length > 0 ? (
                                        <DataGridPro
                                            rows={nonPremiumRejectionDialog.documents}
                                            getRowId={(row: { documentUrl: string; documentName: string }) => row.documentUrl || row.documentName || `doc-${row.documentName}`}
                                            columns={[
                                                { field: 'documentName', headerName: 'Document Name', flex: 2, minWidth: 180 },
                                                { field: 'action', headerName: 'Actions', flex: 0.6, minWidth: 100, sortable: false, renderCell: (params) => (
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Tooltip title="View" arrow><IconButton size="small" sx={{ color: LightBlueBtnColor }} onClick={() => { setDocumentsToView([{ documentUrl: params.row.documentUrl, documentName: params.row.documentName }]); setCurrentDocumentIndex(0); setDocumentViewerOpen(true); }}><FaRegEye /></IconButton></Tooltip>
                                                        <Tooltip title="Download" arrow><IconButton size="small" sx={{ color: LightBlueBtnColor }} onClick={() => downloadSingleDocument(params.row.documentUrl, params.row.documentName)}><FaDownload /></IconButton></Tooltip>
                                                    </Box>
                                                ) },
                                            ]}
                                            hideFooter
                                            disableSelectionOnClick
                                            sx={{ border: 0 }}
                                        />
                                    ) : (
                                        <Typography sx={{ color: '#666', textAlign: 'center', py: 3 }}>No documents available</Typography>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                        <Button onClick={handleCloseNonPremiumRejectionInfo} variant="outlined" sx={{ backgroundColor: '#E0E0E0', color: '#000', borderColor: '#ccc' }}>Cancel</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* document viewer */}
            <DocumentViewer open={documentViewerOpen}
                documents={documentsToView}
                onClose={handleCloseDocumentViewer}
            />

            {/* upload report tag documents */}
            <UploadReportTagDocuments uploadDocumentsOpen={uploadReportTagDocumentsOpen} 
                handleCloseUploadDocuments={handleCloseUploadReportTagDocuments} 
                reportTag={reportTag as ReportTagModel}
                readOnlyTag={Boolean(
                    reportTag?.clientInHouseCloseRequestedAt ??
                    customerInbox.find((r) => r.reportTagId === clientDocumentData.reportTagId)?.clientInHouseCloseRequestedAt
                )}
                clientDocumentData={clientDocumentData} 
                actionFunction={() => {
                    fetchCustomerInbox();
                }} 
            />

            {/* upload signature request documents */}
            <UploadSignatureRequestDocuments
                open={uploadSignatureRequestOpen}
                onClose={handleCloseUploadSignatureRequest}
                signatureRequestId={selectedSignatureRequestIdForUpload}
                onSuccess={fetchCustomerInbox}
            />
        </Box>
        </LocalizationProvider>
    );
};